import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, CircleSlash } from 'lucide-react';
import { TIERS } from '../lib/quoteEstimator';
import { normalizeTier, tierSummary } from '../data/pricingTiers';
import PageHeader from '../components/ui/PageHeader';

const a = 'text-primary-300 hover:text-primary-200 underline underline-offset-4';

const TierDetail: React.FC = () => {
  const { tier: tierParam } = useParams<{ tier: string }>();
  const tier = normalizeTier(tierParam);

  if (!tier) {
    return (
      <div className="page-inner pb-10">
        <PageHeader
          title="Tier not found"
          lead="That development tier does not exist. Compare all seven tiers to find the one you need."
        />
        <Link to="/pricing" className="btn btn-primary">
          Compare all 7 tiers
        </Link>
      </div>
    );
  }

  const t = tierSummary(tier);
  const index = TIERS.indexOf(tier);
  const nextTier = TIERS[index + 1];
  const prevTier = TIERS[index - 1];

  return (
    <div className="page-inner pb-10">
      <Link to="/pricing" className="inline-flex items-center gap-1.5 min-h-[44px] text-sm font-semibold text-primary-300 hover:text-white">
        <ArrowLeft className="w-4 h-4" aria-hidden="true" />
        All development pricing
      </Link>

      <PageHeader
        kicker={<span className="chip-dev">Development · one-time · {t.label}</span>}
        title={`${t.label} — ${t.name}`}
        lead={t.description}
        actions={
          <>
            <Link to="/pricing?view=calculator" className="btn btn-primary">
              Estimate a {t.label} project <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
            <Link to="/plans" className="btn btn-outline">
              See hosting plans
            </Link>
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <div className="space-y-4">
          <section className="panel p-4 sm:p-5" aria-labelledby="price-heading">
            <h2 id="price-heading" className="text-sm font-bold uppercase tracking-wide text-secondary-400">Approved price</h2>
            <p className="mt-2 text-3xl font-bold text-white">{t.priceDisplay}</p>
            <p className="mt-1 text-sm text-secondary-400">{t.priceNote}</p>
            <p className="mt-3 text-sm text-secondary-300">
              The final figure is confirmed after a short scope review. If you own substantial source code, development is{' '}
              <strong className="text-white">50% of this tier's quoted price</strong>. If the source is lost and your site is still online,
              a rebuild from a webscrape is <strong className="text-white">70% of this tier's quoted price</strong>.
            </p>
          </section>

          {t.deepDive && (
            <section className="panel p-4 sm:p-5 border-primary-400/30" aria-labelledby="deep-dive-heading">
              <h2 id="deep-dive-heading" className="text-lg font-bold text-white">{t.deepDive.heading}</h2>
              <p className="mt-3 text-sm font-medium text-primary-100">{t.deepDive.intro}</p>
              <div className="mt-3 space-y-2 text-sm text-secondary-300">
                {t.deepDive.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
              <h3 className="mt-4 text-sm font-bold text-white">Common T5 signals</h3>
              <ul className="mt-2 grid gap-2 sm:grid-cols-2">
                {t.deepDive.signals.map((signal) => (
                  <li key={signal} className="flex gap-2 rounded-xl border border-secondary-800 bg-secondary-900/60 px-3 py-2.5 text-sm text-secondary-200">
                    <span className="text-primary-400" aria-hidden="true">▸</span>
                    <span>{signal}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="panel p-4 sm:p-5" aria-labelledby="reasons-heading">
            <h2 id="reasons-heading" className="text-lg font-bold text-white">Why a project lands in {t.label}</h2>
            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
              {t.reasons.map((reason) => (
                <li key={reason} className="flex gap-2 rounded-xl border border-secondary-800 bg-secondary-900/60 px-3 py-2.5 text-sm text-secondary-200">
                  <span className="text-primary-400" aria-hidden="true">▸</span>
                  <span>{reason}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="panel p-4 sm:p-5" aria-labelledby="includes-heading">
            <h2 id="includes-heading" className="text-lg font-bold text-white">What is included</h2>
            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
              {t.includes.map((feature) => (
                <li key={feature} className="flex gap-2 text-sm text-secondary-200">
                  <Check className="w-4 h-4 mt-0.5 shrink-0 text-success-400" aria-hidden="true" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="panel p-4 sm:p-5" aria-labelledby="fit-heading">
            <h2 id="fit-heading" className="text-lg font-bold text-white">Is this the right fit?</h2>
            <p className="mt-3 text-sm text-secondary-300">
              <span className="font-semibold text-white">Typical projects: </span>
              {t.bestFor}
            </p>
            <p className="mt-2 flex gap-2 text-sm text-secondary-400">
              <CircleSlash className="w-4 h-4 mt-0.5 shrink-0 text-secondary-500" aria-hidden="true" />
              <span><span className="font-semibold text-secondary-300">Look elsewhere if: </span>{t.notFor}</span>
            </p>
          </section>
        </div>

        <aside className="space-y-3 lg:sticky lg:top-24 self-start">
          <div className="rounded-2xl border border-accent-400/40 bg-accent-500/5 p-4">
            <span className="chip-host">Hosting · monthly</span>
            <h2 className="mt-3 text-sm font-bold text-white">Hosting for this tier</h2>
            <p className="mt-1 text-sm text-secondary-300">{t.hostingRange}</p>
            <p className="mt-2 text-xs text-secondary-400">
              Hosting is optional and separate from development. The plan is chosen by what the site needs, not by the development tier name.
            </p>
            <Link to="/plans" className="btn btn-outline mt-3 w-full">
              Compare hosting plans
            </Link>
          </div>

          <div className="panel p-4">
            <h2 className="text-sm font-bold text-white">Where this tier sits</h2>
            <ol className="mt-2 space-y-1 text-sm">
              {TIERS.map((code) => (
                <li key={code}>
                  <Link
                    to={`/tiers/${code}`}
                    aria-current={code === tier ? 'page' : undefined}
                    className={`flex items-center justify-between min-h-[36px] rounded px-2 ${
                      code === tier ? 'bg-primary-500/15 font-semibold text-white' : 'text-secondary-300 hover:text-white'
                    }`}
                  >
                    <span>{code}</span>
                    <span className="text-xs">
                      {tierSummary(code).priceDisplay}
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </aside>
      </div>

      <nav aria-label="Tier pages" className="mt-5 grid gap-3 sm:grid-cols-2">
        {prevTier ? (
          <Link to={`/tiers/${prevTier}`} className="panel p-4 hover:border-primary-400/50">
            <span className="text-xs text-secondary-400">Previous tier</span>
            <span className="mt-1 flex items-center gap-2 font-semibold text-white">
              <ArrowLeft className="w-4 h-4" aria-hidden="true" /> {tierSummary(prevTier).label} — {tierSummary(prevTier).name}
            </span>
          </Link>
        ) : (
          <span />
        )}
        {nextTier ? (
          <Link to={`/tiers/${nextTier}`} className="panel p-4 text-right hover:border-primary-400/50">
            <span className="text-xs text-secondary-400">Next tier</span>
            <span className="mt-1 flex items-center justify-end gap-2 font-semibold text-white">
              {tierSummary(nextTier).label} — {tierSummary(nextTier).name} <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </span>
          </Link>
        ) : (
          <span />
        )}
      </nav>

      <p className="mt-4 text-sm text-secondary-300">
        Still unsure? <Link to="/pricing?view=calculator" className={a}>Use the pricing calculator</Link> or{' '}
        <Link to="/contact?path=development" className={a}>ask us directly</Link>.
      </p>
    </div>
  );
};

export default TierDetail;
